import { useQuery } from "@tanstack/react-query";
import { getTransactionDetailsByTransactionId } from "endpoints/users/userDetailPage.action";
import { Col, Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import { formatDateTime, formatPrice, getTruncatedId } from "lib/utils/formatters";
import { useState } from "react";

interface TransactionDetailModalProps {
    isOpen: boolean;
    toggle: () => void;
    transactionId: string | null;
}

const TransactionDetailModal = ({ isOpen, toggle, transactionId }: TransactionDetailModalProps) => {
    const [copiedId, setCopiedId] = useState<string | null>(null);

    // Copy to clipboard function
    const copyToClipboard = async (fullId: string) => {
        try {
            await navigator.clipboard.writeText(fullId);
            setCopiedId(fullId);
            // Reset the tick icon after 1 second
            setTimeout(() => {
                setCopiedId(null);
            }, 1000);
        } catch (err) {
            console.error('Failed to copy ID:', err);
        }
    };

    // Helper function to get status color
    const getStatusColor = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'paid': return 'bg-success';
            case 'pending': return 'bg-warning';
            case 'failed': return 'bg-danger';
            case 'refunded': return 'bg-info';
            default: return 'bg-secondary';
        }
    };
    const { 
        data,
        isLoading, 
        isError, 
        refetch,
        isRefetching
    } = useQuery({
        queryKey: ['transactionDetailsByUser', transactionId],
        queryFn: () => getTransactionDetailsByTransactionId(transactionId!),
        enabled: !!transactionId && isOpen,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: true,
        retry: 3,
    });

    // If no transaction ID is provided, show pending message
    if (!transactionId) {
        return (
            <Modal isOpen={isOpen} toggle={toggle} size="lg" centered backdrop="static" className="edit-user-modal">
                <ModalHeader toggle={toggle} className="border-bottom-0 pb-0">Transaction Details</ModalHeader>
                <ModalBody className="pt-0 pb-2">
                    <hr className="mt-3 mb-4" />
                    <div className="text-center py-5">
                        <div className="mb-3">
                            <i className="ri-time-line" style={{ fontSize: '3rem', color: '#f7b84b' }}></i>
                        </div>
                        <h5 className="text-warning">Transaction Payment Pending</h5>
                        <p className="text-muted">Transaction details are not available yet. Please check back later.</p>
                    </div>
                </ModalBody>
            </Modal>
        );
    }

    // Loading state
    if (isLoading || isRefetching) {
        return (
            <Modal isOpen={isOpen} toggle={toggle} size="lg" centered backdrop="static" className="edit-user-modal">
                <ModalHeader toggle={toggle} className="border-bottom-0 pb-0">Transaction Details</ModalHeader>
                <ModalBody className="pt-0 pb-2">
                    <hr className="mt-3 mb-4" />
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted">Loading transaction details...</p>
                    </div>
                </ModalBody>
            </Modal>
        );
    }

    // Error state
    if (isError) {
        return (
            <Modal isOpen={isOpen} toggle={toggle} size="lg" centered backdrop="static" className="edit-user-modal">
                <ModalHeader toggle={toggle} className="border-bottom-0 pb-0">Transaction Details</ModalHeader>
                <ModalBody className="pt-0 pb-2">
                    <hr className="mt-3 mb-4" />
                    <div className="text-center py-5">
                        <div className="text-danger mb-3">
                            <i className="ri-error-warning-line" style={{ fontSize: '3rem' }}></i>
                        </div>
                        <h5 className="text-danger">Failed to load transaction details</h5>
                        <p className="text-muted">Please check your connection and try again.</p>
                        <button className="btn btn-primary" onClick={() => refetch()}>
                            <i className="ri-refresh-line me-1"></i>
                            Retry
                        </button>
                    </div>
                </ModalBody>
            </Modal>
        );
    }

    const transactionData = data?.data;
    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" centered backdrop="static" className="edit-user-modal">
            <ModalHeader toggle={toggle} className="border-bottom-0 pb-0">Transaction Details</ModalHeader>
            <ModalBody className="pt-0 pb-2 ">
                <hr className="mt-3 mb-4" />
                <div className="d-flex flex-column gap-2">
                    <Row>
                        <Col>
                            <h6 className="fw-bold">{transactionData?.type || 'Earnest Deposit'}</h6>
                        </Col>
                    </Row>
                    <Row className="flex flex-row justify-content-between w-full">
                        <Col className="flex flex-column">
                            <h6 className="fw-bold">Transaction ID</h6>
                            <div className="d-flex align-items-center gap-2">
                                <span style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                                    {getTruncatedId(transactionData?.id || transactionId)}
                                </span>
                                {(transactionData?.id || transactionId) && (
                                    <span
                                        onClick={() => copyToClipboard(transactionData?.id || transactionId!)}
                                        style={{
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            color: copiedId === (transactionData?.id || transactionId) ? '#28a745' : '#6b7280',
                                            transition: 'color 0.2s ease'
                                        }}
                                        title={copiedId === (transactionData?.id || transactionId) ? 'Copied!' : 'Copy full ID'}
                                    >
                                        {copiedId === (transactionData?.id || transactionId) ? (
                                            <i className="ri-check-line"></i>
                                        ) : (
                                            <i className="ri-file-copy-line"></i>
                                        )}
                                    </span>
                                )}
                            </div>
                        </Col>
                        <Col className="flex flex-column">
                            <h6 className="fw-bold">Invoice NO</h6>
                            <p>{transactionData?.invoice_number || transactionData?.invoice_id || 'N/A'}</p>
                        </Col>
                        <Col className="flex flex-column">
                            <h6 className="fw-bold">Invoice To</h6>
                            <p>Realwealtdy</p>
                        </Col>
                    </Row>
                </div>
                <div className="border rounded mb-3" style={{ overflow: "hidden" }}>
                    <table className="table mb-0">
                        <thead>
                            <tr className="bg-light">
                                <th className="fw-bold border-0" style={{ width: "50%" }}>Status</th>
                                <th className="text-end border-0">
                                    <span className={`badge rounded-pill  ${getStatusColor(transactionData?.status)}`} style={{fontWeight: 500}}>
                                        {transactionData?.status || 'Pending'}
                                    </span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="fw-bold border-0">Amount</td>
                                <td className="text-end border-0">
                                    ${formatPrice(transactionData?.amount || transactionData?.earnest_money || 0)}
                                </td>
                            </tr>
                            <tr>
                                <td className="fw-bold border-0">Date</td>
                                <td className="text-end border-0">
                                    {formatDateTime(transactionData?.createdAt || transactionData?.transaction_date) || 'N/A'}
                                </td>
                            </tr>
                            {/* <tr>
                                <td className="fw-bold border-0">Updated At</td>
                                <td className="text-end border-0">
                                    {formatDateTime(transactionData?.updatedAt) || 'N/A'}
                                </td>
                            </tr> */}
                            <tr>
                                <td className="fw-bold border-0">Payment Method</td>
                                <td className="text-end border-0">
                                    {transactionData?.status === 'pending'? 'N/A':'Bank Transfer via Plaid' }
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </ModalBody>
        </Modal>
    )
}

export default TransactionDetailModal;